import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useRef, useState, useCallback, useContext, createContext, useEffect } from 'react';
import { noop } from "../../utils/noop";
var defaultMovingTowardSubmenContext = [false, noop];
var MovingTowardSubmenuContext = createContext(defaultMovingTowardSubmenContext);
/**
 * Context provider component for a group of list items
 * which could potentially have their selection state
 * temporarily prevented.
 */

export function MovingTowardSubmenuProvider(_ref) {
  var children = _ref.children;
  var preventSelection = useState(false);
  return /*#__PURE__*/React.createElement(MovingTowardSubmenuContext.Provider, {
    value: preventSelection
  }, children);
}
/**
 * Determine the angle between two points
 * @param {{x, y}} p1 Point 1
 * @param {{x, y}} p2 Point 2
 */

function getAngle(p1, p2) {
  return Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180 / Math.PI;
}
/**
 * Determine if the user is likely moving towards an open submenu. This
 * function tries to determine the angle from the cursor position
 * to the top and bottom bounds of the submenu. If the cursor
 * is moving between those bounds, we assume the user is moving
 * towards the submenu.
 *
 * @param {{x: Number, y: Number}} prevCoordinates max-length of 5
 * @param {Event} e onMouseMove event
 * @param {Object} submenuBounds boundingClientRect of expanded submenu
 */


function determineIfMovingTowardSubmenu(previousPoint, e, submenuBounds) {
  if (!submenuBounds || !previousPoint) {
    return false;
  }

  var currentAngle = getAngle(previousPoint, {
    x: e.clientX,
    y: e.clientY
  });
  var isLeft = submenuBounds.isLeft,
      top = submenuBounds.top,
      left = submenuBounds.left,
      right = submenuBounds.right,
      bottom = submenuBounds.bottom;

  if (currentAngle === 0) {
    return isLeft ? e.clientX < previousPoint.x : e.clientX > previousPoint.x;
  }

  var x = isLeft ? right : left;
  var bottomAngle = getAngle(previousPoint, {
    x: x,
    y: bottom
  });
  var topAngle = getAngle(previousPoint, {
    x: x,
    y: top
  });

  if (isLeft) {
    return currentAngle > 0 ? currentAngle > bottomAngle : currentAngle < topAngle;
  }

  return currentAngle < bottomAngle && currentAngle > topAngle;
}
/**
 * Track mouse movements and determine if the user is moving
 * towards a submenu upon the cursor leaving the target. This is used
 * to determine if a submenu should collapse when the cursor
 * leave the target element.
 */


export function useMovingTowardSubmenu(_ref2) {
  var _ref2$delay = _ref2.delay,
      delay = _ref2$delay === void 0 ? 200 : _ref2$delay,
      id = _ref2.id,
      _ref2$enabled = _ref2.enabled,
      enabled = _ref2$enabled === void 0 ? false : _ref2$enabled,
      popoverRef = _ref2.popoverRef;
  var isMovingTowardSubmenu = useRef(false);
  var previousMouseCoordinates = useRef(null);
  var timer = useRef(null);
  var boundsRef = useRef(null);

  var _useContext = useContext(MovingTowardSubmenuContext),
      _useContext2 = _slicedToArray(_useContext, 2),
      preventSelection = _useContext2[0],
      setPreventSelection = _useContext2[1];

  useEffect(function () {
    return function () {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);
  /**
   * if the user quickly moves their cursor towards the submenu,
   * but then returns their cursor to this MenuItem, reset
   * the movement state.
   */

  var onMouseEnter = useCallback(function () {
    if (preventSelection === id) {
      setPreventSelection(false);

      if (timer.current) {
        clearTimeout(timer.current);
      }
    }
  }, [id, preventSelection, setPreventSelection]);
  /**
   * Use the mouse-move event to determine if the user is
   * moving their cursor in the direction of a submenu. We record
   * the previous mouse coordinates and compare them with the
   * current mouse coordinates to determine the angle of movement.
   */

  var onMouseMove = useCallback(function (e) {
    if (!enabled) {
      return;
    }

    if (previousMouseCoordinates.current) {
      if (popoverRef.current && !boundsRef.current) {
        // This is a bit naive. We are reading the popper position
        // from the DOM. Ideally we'd pass this position from
        // popperjs through react state instead.
        var placement = popoverRef.current.dataset.position;
        var isLeft = placement && placement.indexOf('left') > -1;
        var currentBounds = popoverRef.current.getBoundingClientRect();
        boundsRef.current = {
          left: currentBounds.left,
          top: currentBounds.top,
          bottom: currentBounds.bottom,
          right: currentBounds.right,
          isLeft: isLeft
        };
      } // Both Firefox and Chrome only update the position of mouse coordinates
      // when they actually change. Safari can actually send the same
      // coordinates in sequential events, so we need to ingore these
      // duplicate events.


      if (previousMouseCoordinates.current.x === e.clientX && previousMouseCoordinates.current.y === e.clientY) {
        return;
      }

      isMovingTowardSubmenu.current = determineIfMovingTowardSubmenu(previousMouseCoordinates.current, e, boundsRef.current);
    }

    previousMouseCoordinates.current = {
      x: e.clientX,
      y: e.clientY
    };

    if (isMovingTowardSubmenu.current && !preventSelection) {
      setPreventSelection(id);
    } else if (!isMovingTowardSubmenu.current && preventSelection === id) {
      setPreventSelection(false);
    }
  }, [enabled, id, popoverRef, preventSelection, setPreventSelection]);
  /**
   * When the cursor leaves and we have prevented selection during
   * mouse-move, then set a timer before we enable selection.
   */

  var onMouseLeave = useCallback(function () {
    previousMouseCoordinates.current = null;
    boundsRef.current = null;

    if (preventSelection === id) {
      timer.current = window.setTimeout(function () {
        setPreventSelection(false);
      }, delay);
    }
  }, [id, preventSelection, delay, setPreventSelection]);
  return {
    isMovingTowardSubmenu: isMovingTowardSubmenu,
    onMouseMove: onMouseMove,
    onMouseLeave: onMouseLeave,
    onMouseEnter: onMouseEnter,
    preventSelection: preventSelection
  };
}
export var testExports = {
  determineIfMovingTowardSubmenu: determineIfMovingTowardSubmenu
};